import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router';
import {
  CircleAlert,
  CircleCheck,
  Download,
  House,
  Infinity as Infinito,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import {
  forzarActualizacion,
  recargar,
  irAlInicio,
  hayConexion,
  estadoServiceWorker,
} from '@/lib/recovery';
import { descargarArchivo } from '@/lib/file-transfer';
import {
  ETIQUETAS_MOTIVO,
  leerCopia,
  listarCopias,
  nombreArchivoCopia,
  type ResumenCopia,
} from '@/db/copias';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { NOMBRE_BASE_DATOS } from '@/db/schema';
import { serializarRespaldo } from '@/db/backup-format';
import {
  ErrorCopiaAntesDeActualizar,
  exportarBaseDinamica,
  nombreArchivoRescate,
} from '@/db/proteccion';

/**
 * Pantalla de recuperación cuando la app falla (Req. 3).
 *
 * Su razón de existir: la app está configurada para PREGUNTAR antes de
 * actualizar (Req. 2.4), así que el botón de actualizar vive dentro de ella. Si
 * el fallo ocurre antes de poder mostrarlo, no habría salida y habría que
 * borrar el ícono, lo que en iOS BORRA TAMBIÉN LOS DATOS.
 *
 * Aquí siempre hay una salida, y ninguna opción toca los datos: los cachés
 * guardan los archivos del programa; los datos viven en IndexedDB.
 *
 * Origen: Control de Negocio de Comida, con los textos adaptados.
 */
export function ErrorScreen({ error }: { error: unknown }) {
  const [conexion, setConexion] = useState(hayConexion());
  const [estadoSw, setEstadoSw] = useState('consultando…');
  const [trabajando, setTrabajando] = useState(false);
  const [avisoFallo, setAvisoFallo] = useState<string | null>(null);

  useEffect(() => {
    void estadoServiceWorker().then(setEstadoSw);

    const alCambiar = () => setConexion(hayConexion());
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    return () => {
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
    };
  }, []);

  async function alActualizar() {
    setTrabajando(true);
    setAvisoFallo(null);
    const resultado = await forzarActualizacion();
    if (!resultado.exito) {
      setAvisoFallo(resultado.mensaje);
      setTrabajando(false);
    }
  }

  return (
    <div className="mx-auto min-h-dvh w-full max-w-lg space-y-5 bg-fondo px-[18px] pb-segura pt-segura min-[401px]:px-[23px]">
      {/* Fuera del enrutador: la marca sin enlaces, para que esta pantalla nunca dependa de él */}
      <div className="flex items-center gap-1.5 pt-[22px] text-[20px] font-medium tracking-[-0.9px] text-texto">
        <Infinito className="size-[25px] text-marca" />
        DuoFit
      </div>
      <div className="space-y-3">
        <Banner tono="alerta" titulo="La app no pudo continuar">
          Algo falló, pero <strong>tus datos siguen guardados</strong>. Ninguna de las
          opciones de abajo los borra.
        </Banner>
        {error instanceof ErrorCopiaAntesDeActualizar && (
          <Banner tono="aviso" titulo="La actualización de tus datos se detuvo">
            {error.message}
          </Banner>
        )}
      </div>

      <DescargarMisDatos error={error} />

      <div className="space-y-2">
        <Button
          disabled={trabajando || !conexion}
          icono={RefreshCw}
          onClick={() => {
            void alActualizar();
          }}
        >
          {trabajando ? 'Actualizando…' : 'Recargar y actualizar'}
        </Button>
        <p className="px-1 text-[12px] text-texto-suave">
          {conexion
            ? 'Descarta los archivos guardados del programa y baja la versión más reciente. Tus entrenamientos, pesajes, comidas y ayunos no se tocan.'
            : 'Necesita conexión a internet. Sin señal, descartar los archivos dejaría la app sin poder abrirse.'}
        </p>

        <Button variante="secundario" disabled={trabajando} onClick={recargar} icono={RotateCcw}>
          Solo recargar
        </Button>
        <Button variante="secundario" disabled={trabajando} onClick={irAlInicio} icono={House}>
          Volver al inicio
        </Button>
      </div>

      {avisoFallo !== null && (
        <Banner tono="aviso" titulo="No se pudo actualizar">
          {avisoFallo}
        </Banner>
      )}

      <CopiasEnRecuperacion />


      <Banner tono="aviso" titulo="No borres el ícono de la app">
        En iPhone, eliminar el ícono de la pantalla de inicio{' '}
        <strong>borra también todos tus datos</strong>. Si nada de lo anterior funciona, no
        la desinstales: pide ayuda primero.
      </Banner>

      {/* Desplegable (Req. 3.4): el detalle sirve para pedir ayuda, no para leerlo siempre */}
      <details className="rounded-panel bg-superficie px-5 py-2">
        <summary className="flex min-h-11 cursor-pointer items-center text-[14px] font-medium text-texto">
          Detalle técnico
        </summary>
        <dl className="mt-2 space-y-1 text-[12px] text-texto-suave">
          <div className="flex justify-between gap-3">
            <dt>Conexión</dt>
            <dd className="font-medium">{conexion ? 'con internet' : 'sin internet'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Service Worker</dt>
            <dd className="font-medium">{estadoSw}</dd>
          </div>
        </dl>
        <pre className="mb-3 mt-3 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-superficie-suave p-2 font-mono text-[11px] text-texto-suave">
          {describirError(error)}
        </pre>
      </details>
    </div>
  );
}

/**
 * "Descargar mis datos" (Req. 2.10, 3): un respaldo de lo que haya en la base
 * del teléfono, leída sin declarar esquema, para que los datos se puedan
 * sacar aunque la app no logre abrirlos. Si el error ya trae los datos (la
 * copia antes de actualizar no se pudo guardar), se entregan esos.
 */
function DescargarMisDatos({ error }: { error: unknown }) {
  const [trabajando, setTrabajando] = useState(false);
  const [mensaje, setMensaje] = useState<{ ok: boolean; texto: string } | null>(null);

  async function descargar() {
    setTrabajando(true);
    setMensaje(null);
    try {
      if (error instanceof ErrorCopiaAntesDeActualizar) {
        descargarArchivo(error.nombreArchivo, error.respaldo);
        setMensaje({ ok: true, texto: `Se descargó ${error.nombreArchivo}.` });
        return;
      }
      const archivo = await exportarBaseDinamica(NOMBRE_BASE_DATOS);
      const nombre = nombreArchivoRescate();
      descargarArchivo(nombre, serializarRespaldo(archivo));
      setMensaje({ ok: true, texto: `Se descargó ${nombre}.` });
    } catch (e) {
      setMensaje({
        ok: false,
        texto:
          e instanceof Error && e.name === 'DatabaseClosedError'
            ? 'No se pudo leer la base de datos.'
            : e instanceof Error && e.name === 'NoSuchDatabaseError'
              ? 'En este teléfono no hay datos guardados de DuoFit.'
              : `No se pudieron leer los datos: ${e instanceof Error ? e.message : 'error desconocido'}`,
      });
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-[17px] tracking-[-0.25px] text-texto">
          Saca tus datos antes de intentar otra cosa
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-texto-suave">
          Descarga un archivo con todo lo que hay guardado en este teléfono. Sirve para restaurar
          como cualquier respaldo, en esta versión o en una más nueva.
        </p>
      </div>
      <Button disabled={trabajando} onClick={() => void descargar()} icono={Download}>
        {trabajando ? 'Leyendo tus datos…' : 'Descargar mis datos'}
      </Button>
      {mensaje !== null && (
        <p
          className={`flex items-start gap-1.5 text-[13px] font-medium ${mensaje.ok ? 'text-exito' : 'text-alerta'}`}
        >
          {mensaje.ok ? (
            <CircleCheck className="mt-0.5 size-4" />
          ) : (
            <CircleAlert className="mt-0.5 size-4" />
          )}
          {mensaje.texto}
        </p>
      )}
    </Card>
  );
}

/**
 * Copias internas descargables desde la recuperación (Req. 2.10; design.md,
 * 10.6). Viven en una base separada, así que se pueden leer aunque la base
 * principal no abra, por ejemplo si falló la migración de una versión nueva.
 */
function CopiasEnRecuperacion() {
  const [copias, setCopias] = useState<ResumenCopia[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    listarCopias()
      .then((lista) => {
        if (vigente) setCopias(lista);
      })
      .catch(() => {
        // Sin copias que mostrar: la pantalla sigue ofreciendo las demás salidas.
      });
    return () => {
      vigente = false;
    };
  }, []);

  if (copias.length === 0) return null;

  async function descargar(motivo: ResumenCopia['motivo']) {
    setError(null);
    try {
      const copia = await leerCopia(motivo);
      if (copia === undefined) throw new Error('La copia ya no existe.');
      descargarArchivo(nombreArchivoCopia(copia), copia.contenido);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo descargar la copia.');
    }
  }

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-[17px] tracking-[-0.25px] text-texto">
          Copias de seguridad de este teléfono
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-texto-suave">
          La app guardó estas copias antes de restaurar o de actualizar tus datos. Descárgalas y
          guárdalas en Archivos: sirven para restaurar como cualquier respaldo.
        </p>
      </div>
      {copias.map((copia) => (
        <Button
          key={copia.id}
          variante="secundario"
          icono={Download}
          onClick={() => void descargar(copia.motivo)}
        >
          {ETIQUETAS_MOTIVO[copia.motivo]} ({describirUltimoRespaldo(copia.creadaEn)})
        </Button>
      ))}
      {error !== null && (
        <p className="flex items-start gap-1.5 text-[13px] font-medium text-alerta">
          <CircleAlert className="mt-0.5 size-4" />
          {error}
        </p>
      )}
    </Card>
  );
}

/**
 * Versión para el enrutador: toma el error del contexto de rutas.
 * Se separa de ErrorScreen para que la pantalla se pueda reutilizar fuera del
 * enrutador, por ejemplo si falla la apertura de la base de datos al arrancar.
 */
export function RouteErrorScreen() {
  const error = useRouteError();
  return <ErrorScreen error={error} />;
}

function describirError(error: unknown): string {
  if (error === undefined || error === null) return 'Sin detalle disponible.';

  if (error instanceof Error) {
    const pila = error.stack ?? '';
    return `${error.name}: ${error.message}${pila === '' ? '' : `\n\n${pila}`}`;
  }

  if (typeof error === 'object') {
    const posible = error as { status?: number; statusText?: string; data?: unknown };
    if (posible.status !== undefined) {
      return `${posible.status} ${posible.statusText ?? ''}\n${String(posible.data ?? '')}`.trim();
    }
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }

  return String(error);
}
